import React, { useCallback, useEffect, useState } from 'react';
import { prop } from 'ramda';
import { Loader } from 'js/components/common/Loader';
import { useGetTemporaryAuthenticationToken } from 'js/hooks/auth';
import { useUserSelector } from 'js/store/hooks';
import { getScript } from 'js/utils/scripts.util';
import { useLoginPopup } from 'js/components/popups/LoginPopup';
import { withComponent } from 'js/components/hocs/withComponent';
import { useComponentConfig, useComponentMethods, useComponentTranslations } from 'js/hooks/component';

import * as helpers from './helpers';
import * as hooks from './hooks';

import style from './Kambi.scss';

export const KambiBare = () => {
    const translate = useComponentTranslations();
    const { showComponentError } = useComponentMethods();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const [kambiContainer, setKambiContainer] = useState(null);
    const kambiContainerRef = useCallback((node) => setKambiContainer(node), []);
    const [kambiLoaded, setKambiLoaded] = useState(false);
    const [kambiInitialized, setKambiInitialized] = useState(false);
    const getTemporaryAuthenticationToken = useGetTemporaryAuthenticationToken();
    const showLoginPopup = useLoginPopup();
    const {
        bootstrapURL,
        widgetAPIURL = 'https://kambi-widget-api.bc.kambicdn.com/kambi-widget-api.js',
        initialRoute
    } = useComponentConfig();
    const kambiCustomerSettings = hooks.useKambiCustomerSettings();
    const kambiConfig = hooks.useKambiConfig();

    useEffect(() => {
        if (!bootstrapURL) {
            showComponentError(translate('configError'));
        }
    }, [showComponentError, translate, bootstrapURL]);

    useEffect(() => {
        if (!kambiContainer) {
            return;
        }

        const parent = kambiContainer.parentNode;

        parent.removeChild(kambiContainer);

        if (helpers.existKambiDOM()) {
            setKambiInitialized(true);
        }

        helpers.attachKambiDOM(parent, kambiContainer);

        setKambiLoaded(true);
    }, [
        kambiContainer,
        setKambiLoaded,
        setKambiInitialized
    ]);

    useEffect(() => {
        if (!kambiContainer || !kambiLoaded) {
            return;
        }

        (async () => {
            if (kambiInitialized) {
                return;
            }

            helpers.setConfig({
                config: kambiConfig,
                customerSettings: kambiCustomerSettings,
                showLoginPopup,
                setKambiInitialized
            });

            await getScript(bootstrapURL);

            helpers.navigateHome({ initialRoute });
        })();
    }, [
        kambiConfig,
        kambiCustomerSettings,
        kambiContainer,
        kambiInitialized,
        setKambiInitialized,
        kambiLoaded,
        bootstrapURL,
        initialRoute,
        showLoginPopup
    ]);

    useEffect(() => {
        return () => {
            helpers.detachKambiDOM();
        };
    }, []);

    useEffect(() => {
        if (!isLoggedIn || !kambiInitialized) {
            return;
        }

        (async () => {
            await getScript(widgetAPIURL);

            const wapi = await window.KambiWidget.ready;
            const token = await getTemporaryAuthenticationToken();

            wapi.request(wapi.LOGIN, {
                ...kambiConfig,
                punterId: 'playtech@operator',
                ticket: token
            });
        })();

        return () => {
            (async () => {
                const wapi = await window.KambiWidget.ready;

                wapi.request(wapi.LOGOUT);
            })();
        };
    }, [
        kambiLoaded,
        bootstrapURL,
        isLoggedIn,
        kambiConfig,
        widgetAPIURL,
        kambiInitialized,
        getTemporaryAuthenticationToken
    ]);

    if (!bootstrapURL) {
        return null;
    }

    // #KambiBC is required id by Kambi
    return (
        <div id="KambiBC" ref={kambiContainerRef} className={`${style.container} cms-kambi`}>
            <div className={style.loadingContainer}>
                <Loader />
            </div>
        </div>
    );
};

export const Kambi = withComponent()(KambiBare);

export default Kambi;
